import { onScopeDispose, watch } from 'vue';
import { UNKNOWN_ALBUM_ART_PATH } from '@/constants/coverArt';
import type { useAudioPlaybackStore } from '@/stores/playerStore';

type PlaybackStore = ReturnType<typeof useAudioPlaybackStore>;

/** The placeholder's pixel size, so the OS can score it without loading it. */
const FALLBACK_ARTWORK_SIZES = '480x480';

/** Built with `sizes`/`type` only when we know them; the OS copes without. */
type MediaArtwork = { src: string; sizes?: string; type?: string };

/**
 * Publish what the player is doing to the OS media controls.
 *
 * Without this, the phone's lock screen and the laptop's media keys see a
 * nameless `<audio>` element on a page called "Tabasco": no title, no artist,
 * no art, and no way to skip. This tells `navigator.mediaSession` the same
 * three identity lines and cover the bar shows, keeps the scrubber and the
 * play/pause glyph in step, and routes the hardware buttons back into the
 * store — so Next on the lock screen is the same Next as the bar's.
 *
 * It lives next to the `<audio>` element (AudioPlayer.vue owns it) rather than
 * in the store because it is an OS side effect with a lifetime, not state: the
 * watchers stop and the handlers come off when the player unmounts.
 *
 * Every call into the API is guarded. Older Safari throws on an action it does
 * not know, `setPositionState` is missing on some engines, and jsdom has none
 * of it — so a missing feature degrades to "that one control doesn't show",
 * never to a crash in the player.
 */
export function useMediaSession(
    playback: PlaybackStore,
    mediaSession: MediaSession | null = readMediaSession()
): void {
    if (mediaSession === null) {
        return;
    }
    // Narrowed once, so the closures below need no re-checking.
    const session: MediaSession = mediaSession;

    // Every action that currently has a handler, so disposal can take down
    // exactly what was put up — a hand-kept list drifts the day someone adds
    // an action and forgets it, leaving a leaked handler calling into the
    // store from an unmounted player.
    const registeredActions = new Set<MediaSessionAction>();

    function setHandler(action: MediaSessionAction, handler: MediaSessionActionHandler | null): void {
        try {
            session.setActionHandler(action, handler);
            if (handler === null) {
                registeredActions.delete(action);
            } else {
                registeredActions.add(action);
            }
        } catch {
            // This engine does not support the action. The control stays
            // hidden; nothing else is affected.
        }
    }

    // ---- metadata: the three identity lines and the art ----------------------

    // Which cover URL the newest probe below was started for. A probe that
    // fails after the track has moved on compares against this and does
    // nothing — only the CURRENT cover may be downgraded.
    let probedCoverUrl: string | null = null;

    watch(
        () => [
            playback.visible,
            playback.trackTitle,
            playback.trackCodeUpper,
            playback.trackArtist,
            playback.trackAlbum,
            playback.coverArtUrl,
        ],
        () => {
            if (!playback.visible) {
                probedCoverUrl = null;
                session.metadata = null;

                return;
            }

            session.metadata = buildMetadata(playback);
            probeCoverArt(playback.coverArtUrl);
        },
        { immediate: true }
    );

    /**
     * Check the published cover actually loads, and downgrade if not.
     *
     * A signed cover URL can outlive its signature in a long-lived tab. The
     * bar self-heals through its image-error fallback, but the OS is handed
     * one URL with no fallback of its own (engines are not required to fall
     * through a failed artwork entry) — so probe the same URL here and
     * republish with the placeholder art when it no longer loads. Same URL as
     * the bar's <img>, so the probe is normally answered from cache.
     */
    function probeCoverArt(coverArtUrl: string | null): void {
        probedCoverUrl = coverArtUrl;
        if (coverArtUrl === null || typeof Image === 'undefined') {
            return;
        }

        const probe = new Image();
        probe.onerror = () => {
            if (probedCoverUrl !== coverArtUrl || !playback.visible) {
                return;
            }

            session.metadata = buildMetadata(playback, { coverUnloadable: true });
        };
        probe.src = coverArtUrl;
    }

    // ---- playback state: the play/pause glyph ----------------------------

    watch(
        () => [playback.visible, playback.mode, playback.isPaused],
        () => {
            session.playbackState = derivePlaybackState(playback);
        },
        { immediate: true }
    );

    // ---- position: the scrubber -------------------------------------------

    // Re-sent on every timeupdate. The OS interpolates between updates on its
    // own, but re-sending is what keeps the scrubber honest after a seek, and
    // the store has no separate "seeked" signal to hang that on. Play/pause is
    // NOT a source: it changes the glyph (previous watcher), not the position.
    watch(
        () => [playback.durationSeconds, playback.currentTimeSeconds],
        () => {
            publishPositionState(session, playback);
        },
        { immediate: true }
    );

    // ---- transport: the buttons -----------------------------------------------

    // Mapped 1:1 onto the store's own play/pause, with no guards here. The
    // obvious guard — consult the isPaused mirror — goes stale on error paths
    // (a decode error fires `error` but never `pause`), and a Pause that
    // trusted it reloaded and REPLAYED the failed track. The store pair asks
    // the element and the mode directly, and knows a download is not a thing
    // `el.play()` can be called over.
    setHandler('play', () => {
        void playback.play();
    });

    setHandler('pause', () => {
        playback.pause();
    });

    // Previous/Next are registered whenever the player is showing, NOT only
    // while the store can honour them, and there are deliberately no
    // seekbackward/seekforward handlers. Both are for iOS: its lock screen
    // shows the skip-interval buttons INSTEAD of Previous/Next when those
    // handlers exist, and it builds its control set when the session starts
    // and does not reliably redraw when handlers are swapped mid-track. So a
    // Next pressed at the end of the queue is a no-op — `playNext` already
    // returns when there is nothing to go to — rather than a button that
    // comes and goes. Scrubbing still works through `seekto`.
    watch(
        () => playback.visible,
        (visible) => {
            setHandler('nexttrack', visible ? () => void playback.playNext() : null);
            setHandler('previoustrack', visible ? () => void playback.playPrevious() : null);
        },
        { immediate: true }
    );

    setHandler('seekto', (details) => {
        if (typeof details.seekTime !== 'number') {
            return;
        }
        seekToSeconds(playback, details.seekTime);
    });

    onScopeDispose(() => {
        for (const action of [...registeredActions]) {
            setHandler(action, null);
        }
        session.metadata = null;
        session.playbackState = 'none';
        clearPositionState(session);
    });
}

function readMediaSession(): MediaSession | null {
    if (typeof navigator === 'undefined') {
        return null;
    }
    if (!('mediaSession' in navigator)) {
        return null;
    }

    return navigator.mediaSession ?? null;
}

function buildMetadata(
    playback: PlaybackStore,
    options: { coverUnloadable?: boolean } = {}
): MediaMetadata | null {
    if (typeof MediaMetadata === 'undefined') {
        return null;
    }

    return new MediaMetadata({
        // The bar's own title rule — one source, so they cannot diverge.
        title: playback.displayTitle,
        artist: playback.trackArtist ?? '',
        album: playback.trackAlbum ?? '',
        artwork: buildArtwork(options.coverUnloadable ? null : playback.coverArtUrl),
    });
}

function buildArtwork(coverArtUrl: string | null): MediaArtwork[] {
    if (coverArtUrl) {
        // A signed S3 URL. We do not know its pixel size or type, and the
        // spec makes both optional; the OS loads it and reads them itself.
        return [{ src: coverArtUrl }];
    }

    return [
        {
            src: absoluteUrl(UNKNOWN_ALBUM_ART_PATH),
            sizes: FALLBACK_ARTWORK_SIZES,
            type: 'image/png',
        },
    ];
}

/**
 * The fallback art is served from this origin. It is made absolute rather
 * than left path-relative because some engines hand the artwork URL to an OS
 * process that has no document to resolve it against.
 */
function absoluteUrl(path: string): string {
    if (typeof window === 'undefined') {
        return path;
    }

    return new URL(path, window.location.origin).href;
}

function derivePlaybackState(playback: PlaybackStore): MediaSessionPlaybackState {
    if (!playback.visible) {
        return 'none';
    }
    if (playback.mode === 'idle') {
        return 'none';
    }
    if (playback.isPaused) {
        return 'paused';
    }

    return 'playing';
}

function publishPositionState(mediaSession: MediaSession, playback: PlaybackStore): void {
    if (typeof mediaSession.setPositionState !== 'function') {
        return;
    }

    const duration = playback.durationSeconds;
    // The store reports 0 while no duration is known: a track still
    // downloading, the player closed, the instant between tracks
    // (stopCurrentPlayback zeroes the timeline on every change). There is
    // nothing to draw — and the PREVIOUS track's state must not linger under
    // the new title, so clear rather than skip.
    if (!Number.isFinite(duration) || duration <= 0) {
        clearPositionState(mediaSession);

        return;
    }

    // A position past the end throws a TypeError; a timeupdate can land just
    // after the duration is trimmed, so clamp rather than trust the ordering.
    const position = Math.min(Math.max(playback.currentTimeSeconds, 0), duration);

    try {
        mediaSession.setPositionState({ duration, position, playbackRate: 1 });
    } catch {
        // The OS rejected the state; the scrubber simply stays where it was.
    }
}

/** The spec's no-argument call resets the OS scrubber to "no position". */
function clearPositionState(mediaSession: MediaSession): void {
    if (typeof mediaSession.setPositionState !== 'function') {
        return;
    }

    try {
        mediaSession.setPositionState();
    } catch {
        // An engine that cannot clear keeps the stale state it already had.
    }
}

function seekToSeconds(playback: PlaybackStore, seconds: number): void {
    const duration = playback.durationSeconds;
    if (!Number.isFinite(duration) || duration <= 0) {
        return;
    }

    playback.seekToFraction(seconds / duration);
}
